import { HotCache } from './hot-cache';

// Globalize is a statically loaded js framework, attached to the window, without type declarations
// See documentation at: https://www.npmjs.com/package/globalize/v/0.1.1
// We have to use an outdated version like this, because this is what powerbi users for formatting
function Globalize() {
  return (<any>window).Globalize;
}

export const ROUNDTRIP_DATETIME_FORMAT = 'yyyy-MM-ddTHH:mm:ss.sssZ';

function normalizeLocaleName(locale: string) {
  let split = locale.split('-', 2);

  if (split.length > 1)
    return `${split[0]}-${split[1].toUpperCase()}`;

  return locale;
}

const globalizeCultureCache: Record<string, any> = {};

function getGlobalizeCulture(locale: string) {
  let localeName = normalizeLocaleName(locale);

  let culture =
    globalizeCultureCache[localeName]
      ??= Globalize().findClosestCulture(localeName) ?? Globalize().findClosestCulture('en-US');

  return culture;
}


const globalizeCache: Record<string, any> = {};

export function getGlobalize(locale: string) {
  let localeName = normalizeLocaleName(locale);

  let globalize =
    globalizeCache[localeName]
      ??= Globalize()(getGlobalizeCulture(locale).name) ?? Globalize()('en-US');

  return globalize;
}

export function resolvePbiDateTimeFormat(locale: string, type: string) {
  let culture = getGlobalizeCulture(locale);
  return culture?.calendar?.patterns[type];
}

export function convertToAngularFormat(globalizeFormat: string) {
  return globalizeFormat.replace(/\btt\b/, 'a');
}

const pbiDateTimeFormatCache = new Map<string, string>();

export function getPbiDateTimeFormat(locale: string, type: string) {
  let cacheKey = `${locale}/${type}`;

  let format = pbiDateTimeFormatCache.get(cacheKey);
  if (format === undefined) {
    format = resolvePbiDateTimeFormat(locale, type);
    pbiDateTimeFormatCache.set(cacheKey, format);
  }

  return format;
}


const cache = new HotCache<string, string>(4);

export function getAngularDateTimeFormatForPbi(locale: string, type: string, fallback: string) {
  let pbiFormat = cache.getOrAdd([locale, type].join('.'), () => {
    let pbiFormat = resolvePbiDateTimeFormat(locale, type);
    if (!pbiFormat)
      return null;

    return convertToAngularFormat(pbiFormat);
  });

  if (!pbiFormat)
    return fallback;

  return pbiFormat;
}

export function angularShortDateFormatFromPbi(locale: string) {
  return getAngularDateTimeFormatForPbi(locale, 'd', 'shortDate');
}

/**
 * Returns format used to stringify a short time value with angular date pipe or formatDate.
 * This is compatile with PBI's localization, which uses an outdated version of the globalize library.
 */
export function angularShortTimeFormatFromPbi(locale: string, includeSeconds: boolean) {
  return getAngularDateTimeFormatForPbi(
    locale,
    includeSeconds ? 'T' : 't',
    includeSeconds ? 'shortTime' : 'mediumTime');
}

/**
 * Returns format used to stringify a date/time value.
 * This is compatile with PBI's localization, which uses an outdated version of the globalize library.
 */
export function angularShortDateTimeFormatFromPbi(locale: string) {
  return `${angularShortDateFormatFromPbi(locale)} ${angularShortTimeFormatFromPbi(locale, true)}`;
}


export interface FormatFieldValueOptions {
  locale?: string;
  roundtripFormat?: boolean;
  quote?: boolean;
  formatInvalid?: boolean;
}

export type DateFormat = 'short' | 'medium';
